import { useQueryClient } from "@tanstack/react-query";
import { startTransition, useCallback, useOptimistic, useState } from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import { useChatMessagesQuery } from "web_myplann/Api/Chat/useChatMessagesQuery";
import { useSendImageMutation } from "web_myplann/Api/Chat/useSendImageMutation";
import { useSendMessageMutation } from "web_myplann/Api/Chat/useSendMessageMutation";
import { createOptimisticChatMessage } from "web_myplann/Chat/Helpers/createOptimisticChatMessage";
import { useChatRecipient } from "web_myplann/Chat/Hooks/useChatRecipient";
import { useMarkChatMessagesRead } from "web_myplann/Chat/Hooks/useMarkChatMessagesRead";
import type { ChatMessageView } from "web_myplann/Chat/Types/chatMessage";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { base64ToFile } from "web_myplann/Infrastructure/Utils/FileUtils";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";
import { device } from "web_myplann/Utils/device";

type AttachmentPicker = () => Promise<{
	payload: { file: { base64: string; filename: string } };
}>;

export type ChatPageState = {
	draftMessage?: string;
};

export const useChatPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { state } = useLocation() as { state: ChatPageState | null };
	const queryClient = useQueryClient();
	const { t } = useResource();
	const routeParams = useParams<{
		conversationId: string;
	}>();

	const [conversationId, setConversationId] = useState(
		getTypedParam(routeParams, "conversationId", Number) ?? null,
	);
	const advisorIdParam = getTypedParam(searchParams, "advisorId", String);
	const {
		advisorId,
		advisorName,
		advisorPhoto,
		isLoading: isRecipientLoading,
	} = useChatRecipient(conversationId, advisorIdParam);

	const [input, setInput] = useState(state?.draftMessage ?? "");

	const {
		data: serverMessages = [],
		isLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useChatMessagesQuery(conversationId);
	useMarkChatMessagesRead(conversationId, serverMessages);

	const [messages, addOptimisticMessage] = useOptimistic(
		serverMessages,
		(currentMessages, optimisticMessage: ChatMessageView) => [
			...currentMessages,
			optimisticMessage,
		],
	);

	const { mutateAsync: sendMessage, isPending: isSending } =
		useSendMessageMutation();
	const { mutate: sendImage, isPending: isSendingImage } =
		useSendImageMutation();

	const invalidateMessages = useCallback(
		(id: number) =>
			queryClient.invalidateQueries({ queryKey: ChatKeys.messages(id) }),
		[queryClient],
	);

	const invalidateList = useCallback(
		() => queryClient.invalidateQueries({ queryKey: ChatKeys.list() }),
		[queryClient],
	);

	const handleSend = () => {
		const content = input.trim();
		if (!content || isSending) return;

		const optimisticMessageId = -Date.now();
		const optimisticMessage = createOptimisticChatMessage(
			optimisticMessageId,
			content,
		);
		setInput("");

		startTransition(async () => {
			addOptimisticMessage(optimisticMessage);

			let item: Awaited<ReturnType<typeof sendMessage>>;
			try {
				item = await sendMessage({ conversationId, advisorId, content });
			} catch {
				setInput(content);
				toast.error(t(Resources.Chat.sendError));
				return;
			}

			const newId = item.chat_conversation;
			if (conversationId === null && newId > 0) {
				setConversationId(newId);
				navigate(Routing.Chat.Detail.path(String(newId), advisorId), {
					replace: true,
				});
				await invalidateMessages(newId);
			} else if (conversationId !== null) {
				await invalidateMessages(conversationId);
			}
			await invalidateList();
		});
	};

	const sendPickedAttachment = async (picker: AttachmentPicker) => {
		if (conversationId === null || isSendingImage) return;

		const response = await picker();
		const base64 = response.payload.file.base64;
		const filename = response.payload.file.filename;
		if (!base64 || !filename) return;

		const caption = input.trim();

		sendImage(
			{
				conversationId,
				file: base64ToFile(base64, filename),
				content: caption,
			},
			{
				onSuccess: () => {
					setInput("");
					invalidateMessages(conversationId);
					invalidateList();
				},
				onError: () => toast.error(t(Resources.Chat.sendError)),
			},
		);
	};

	return {
		advisorName,
		advisorPhoto,
		messages,
		isLoading: isLoading || isRecipientLoading,
		hasOlder: hasNextPage,
		isLoadingOlder: isFetchingNextPage,
		loadOlder: () => fetchNextPage(),
		input,
		setInput,
		onSend: handleSend,
		isSending: isSending || isSendingImage,
		onPickPhoto: () => sendPickedAttachment(device.pickAttachmentPhoto),
		onPickGallery: () => sendPickedAttachment(device.pickAttachmentGallery),
		onPickFile: () => sendPickedAttachment(device.pickAttachmentFile),
		onBack: () => navigate(-1),
	};
};
